<template>
  <div>
    <v-overlay :value="creatingRefund" :z-index="999999">
      <v-col class="align-center justify-center text-center">
        <v-progress-circular :size="120" :width="8" indeterminate color="primary"></v-progress-circular>
        <h3 class="my-3">Generando reembolso...</h3>
      </v-col>
    </v-overlay>

    <!-- Encabezado y búsqueda -->
    <div class="px-6 pt-6 pb-4">
      <h1 class="text-h5 font-weight-bold mb-5">Registrar Reembolso</h1>
      <v-text-field
        v-model="search"
        label="Buscar usuario"
        placeholder="Correo o RUT..."
        outlined
        dense
        hide-details
        append-icon="mdi-magnify"
        style="max-width: 480px"
        @click:append="searchUser"
        @keyup.enter="searchUser"
      ></v-text-field>
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-2" style="max-width: 480px"></v-progress-linear>
    </div>

    <!-- Varios usuarios comparten el mismo correo: elegir el correcto -->
    <div v-if="userCandidates.length" class="px-6 pb-8">
      <v-alert type="warning" outlined dense class="mb-3">
        Encontramos {{ userCandidates.length }} usuarios con ese correo. Selecciona el correcto:
      </v-alert>
      <v-card outlined>
        <v-list two-line>
          <template v-for="(candidate, index) in userCandidates">
            <v-list-item :key="candidate.id" @click="selectCandidate(candidate)">
              <v-list-item-content>
                <v-list-item-title>{{ candidate.name }}</v-list-item-title>
                <v-list-item-subtitle>RUT: {{ candidate.rut || '-' }} · {{ candidate.email }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn text color="primary" :loading="loading">Seleccionar</v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="index < userCandidates.length - 1" :key="`div-${candidate.id}`"></v-divider>
          </template>
        </v-list>
      </v-card>
    </div>

    <div v-if="user" class="px-6 pb-8">

      <!-- Info usuario + banco -->
      <v-card outlined class="mb-4">
        <v-card-text class="pa-5">
          <v-row>
            <v-col cols="12" md="6">
              <div class="d-flex align-center mb-3">
                <v-icon color="primary" size="20" class="mr-2">mdi-account-circle-outline</v-icon>
                <span class="subtitle-2 font-weight-bold primary--text">Datos del usuario</span>
              </div>
              <div class="d-flex align-center mb-2">
                <v-icon size="16" color="grey darken-1" class="mr-2">mdi-account-outline</v-icon>
                <span class="body-2 grey--text text--darken-1 mr-1">Nombre:</span>
                <span class="body-2">{{ user.name }}</span>
              </div>
              <div class="d-flex align-center mb-2">
                <v-icon size="16" color="grey darken-1" class="mr-2">mdi-card-account-details-outline</v-icon>
                <span class="body-2 grey--text text--darken-1 mr-1">RUT:</span>
                <span class="body-2">{{ user.rut }}</span>
              </div>
              <div class="d-flex align-center mb-2">
                <v-icon size="16" color="grey darken-1" class="mr-2">mdi-email-outline</v-icon>
                <span class="body-2 grey--text text--darken-1 mr-1">Email:</span>
                <span class="body-2">{{ user.email }}</span>
              </div>
              <div class="d-flex align-center">
                <v-icon size="16" color="grey darken-1" class="mr-2">mdi-phone-outline</v-icon>
                <span class="body-2 grey--text text--darken-1 mr-1">Tel:</span>
                <span class="body-2">{{ user.phone }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="d-flex align-center mb-3">
                <v-icon color="primary" size="20" class="mr-2">mdi-bank-outline</v-icon>
                <span class="subtitle-2 font-weight-bold primary--text">Datos bancarios</span>
              </div>
              <v-card
                v-if="user.dataBank !== null"
                flat
                color="primary lighten-5"
                style="border-radius: 10px; overflow: hidden; max-width: 340px"
              >
                <div class="px-4 pt-4 pb-2">
                  <p class="body-2 font-weight-bold primary--text mb-1">{{ user.dataBank.bank }}</p>
                  <p class="caption mb-1">{{ user.dataBank.accountType }}</p>
                  <p class="caption mb-0">{{ accountNumberDisplay }}</p>
                </div>
                <v-divider style="border-color: white"></v-divider>
                <div class="pa-4">
                  <div class="d-flex align-center" style="cursor: pointer" @click="openBankModal">
                    <v-icon size="19" color="primary">mdi-file-edit-outline</v-icon>
                    <span class="body-2 font-weight-bold primary--text ml-1">Modificar cuenta</span>
                  </div>
                </div>
              </v-card>
              <v-btn v-else color="green" depressed @click="openBankModal">
                <v-icon color="white">mdi-bank</v-icon>
                <span class="white--text font-weight-bold ml-1">Agregar datos</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Reembolsos existentes -->
      <v-card v-if="loadingRefunds || userRefunds.length > 0" outlined class="mb-4">
        <v-card-text class="pa-5">
          <div class="d-flex align-center mb-2">
            <v-icon color="primary" size="20" class="mr-2">mdi-history</v-icon>
            <span class="subtitle-2 font-weight-bold primary--text">Reembolsos en curso</span>
          </div>
          <v-divider class="mb-4"></v-divider>
          <div v-if="loadingRefunds" class="d-flex justify-center py-4">
            <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
          </div>
          <div v-else>
            <div
              v-for="refund in userRefunds"
              :key="refund.id"
              class="d-flex align-center justify-space-between py-2"
              style="border-bottom: 1px solid #f0f0f0"
            >
              <div>
                <span class="body-2 font-weight-medium">{{ refund.service ? refund.service.title : '—' }}</span>
                <span class="caption grey--text text--darken-1 ml-2">#{{ refund.referenceIndex }}</span>
                <div class="mt-1">
                  <v-chip
                    x-small
                    outlined
                    :color="refund.changelogs && refund.changelogs.status ? refund.changelogs.status.highlightColor : 'grey'"
                  >
                    {{ refund.changelogs && refund.changelogs.status ? refund.changelogs.status.name : '—' }}
                  </v-chip>
                </div>
              </div>
              <v-btn small depressed color="primary" @click="openAddDocsModal(refund)">
                <v-icon left small>mdi-paperclip</v-icon>
                Agregar docs
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Paso 1: Membresías -->
      <v-card outlined class="mb-4">
        <v-card-text class="pa-5">
          <div class="d-flex align-center mb-2">
            <v-icon color="primary" size="20" class="mr-2">mdi-paw</v-icon>
            <span class="subtitle-2 font-weight-bold primary--text">Membresía</span>
          </div>
          <v-divider class="mb-4"></v-divider>
          <div v-if="user.memberships.length > 0">
            <v-radio-group v-model="membershipSelected" class="mt-0">
              <div v-for="membership in user.memberships" :key="membership.id" class="mb-3">
                <v-row align="center">
                  <v-radio :label="membership.name" :value="membership" class="mx-2 my-0"></v-radio>
                  <v-icon left class="teal--text">{{ membership.species === 'cat' ? 'mdi-cat' : 'mdi-dog' }}</v-icon>
                  <membership-status-chip :status="membership.status" />
                  <plan-status-chip :planName="membership.planName" />
                </v-row>
              </div>
            </v-radio-group>
          </div>
          <p v-else class="body-2 grey--text mb-0">No hay membresías para mostrar</p>
        </v-card-text>
      </v-card>

      <div v-if="membershipSelected.name !== ''">

        <!-- Paso 2: Servicio -->
        <v-card outlined class="mb-4">
          <v-card-text class="pa-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" size="20" class="mr-2">mdi-medical-bag</v-icon>
              <span class="subtitle-2 font-weight-bold primary--text">Servicio a reembolsar — {{ membershipSelected.name }}</span>
            </div>
            <v-divider class="mb-4"></v-divider>
            <div v-if="checkingEligibility" class="d-flex align-center py-2">
              <v-progress-circular indeterminate color="primary" size="18" width="2" class="mr-2"></v-progress-circular>
              <span class="caption grey--text">Verificando elegibilidad...</span>
            </div>
            <div v-else-if="membershipSelected.servicesToRefund.length > 0">
              <v-radio-group v-model="selectedService" class="mt-0">
                <template v-for="service in membershipSelected.servicesToRefund">
                  <div
                    v-if="!eligibilityMap[service.id] || eligibilityMap[service.id].allowed"
                    :key="service.id"
                    class="mb-2"
                  >
                    <v-radio
                      :label="service.servicesName"
                      :value="service"
                      class="mb-0"
                    ></v-radio>
                  </div>
                </template>
              </v-radio-group>
            </div>
            <p v-else class="body-2 grey--text mb-0">No hay servicios para mostrar</p>
          </v-card-text>
        </v-card>

        <!-- Paso 3: Fecha -->
        <v-card outlined class="mb-4">
          <v-card-text class="pa-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" size="20" class="mr-2">mdi-calendar</v-icon>
              <span class="subtitle-2 font-weight-bold primary--text">Fecha de solicitud de reembolso</span>
            </div>
            <v-divider class="mb-4"></v-divider>
            <div style="width: fit-content">
              <v-date-picker v-model="selectedDate" :max="today" :width="290" :show-current="false"></v-date-picker>
            </div>
            <p v-if="formSubmitted && !selectedDate" class="caption red--text mt-2">Selecciona una fecha</p>
          </v-card-text>
        </v-card>

        <!-- Paso 4: Documentos (solo si hay adjuntos requeridos) -->
        <v-card v-if="selectedService && requiredAttachments.length > 0" outlined class="mb-4">
          <v-card-text class="pa-5">
            <div class="d-flex align-center">
              <span class="subtitle-2 font-weight-bold primary--text">Documentos requeridos</span>
            </div>
            <v-divider style="margin-top: 16px"></v-divider>
            <v-row class="mt-4">
              <v-col
                v-for="attachment in requiredAttachments"
                :key="attachment.id"
                cols="12"
                md="4"
                class="py-0"
              >
                <v-file-input
                  show-size
                  outlined
                  :label="attachment.name"
                  :rules="[v => !!v || 'Campo obligatorio']"
                  required
                  :error="formSubmitted && !attachmentFiles[attachment.id]"
                  v-model="attachmentFiles[attachment.id]"
                ></v-file-input>
              </v-col>
            </v-row>
            <div v-if="jurAttachment" class="mt-2">
              <v-card outlined :color="jurCardColor" style="cursor: pointer" @click="openJurModal">
                <v-card-text class="pa-4 d-flex align-center">
                  <v-icon size="52" :color="jurColor">mdi-file-document-edit-outline</v-icon>
                  <div class="ml-4 flex-grow-1">
                    <p class="subtitle-2 font-weight-bold mb-0" :class="jurTextClass">{{ jurAttachment.name }}</p>
                    <p v-if="jurSubtitle" class="caption mb-0" :class="jurTextClass">{{ jurSubtitle }}</p>
                  </div>
                  <v-icon :color="jurColor">mdi-chevron-right</v-icon>
                </v-card-text>
              </v-card>
            </div>
            <template v-if="optionalAttachments.length > 0">
              <div class="mt-10 d-flex align-center">
                <span class="subtitle-2 font-weight-bold primary--text">Documentos opcionales</span>
              </div>
              <v-divider style="margin-top: 16px"></v-divider>
              <v-row class="mt-4">
                <v-col
                  v-for="attachment in optionalAttachments"
                  :key="attachment.id"
                  cols="12"
                  md="4"
                  class="py-0"
                >
                  <v-file-input
                    show-size
                    outlined
                    multiple
                    chips
                    small-chips
                    :label="attachment.name"
                    v-model="optionalAttachmentFiles[attachment.id]"
                  ></v-file-input>
                </v-col>
              </v-row>
            </template>
            <div class="mt-10 d-flex align-center">
              <span class="subtitle-2 font-weight-bold primary--text">Comentarios</span>
            </div>
            <v-divider style="margin-top: 16px"></v-divider>
            <v-row class="mt-4">
              <v-col cols="12" class="py-0">
                <v-textarea
                  v-model="comments"
                  outlined
                  dense
                  rows="3"
                  hide-details
                  placeholder="Utiliza este espacio para añadir información que facilite la revisión de la solicitud"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Paso 5: Documentación a tu nombre -->
        <v-card v-if="selectedService" outlined class="mb-4">
          <v-card-text class="pa-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" size="20" class="mr-2">mdi-file-account-outline</v-icon>
              <span class="subtitle-2 font-weight-bold primary--text">¿La documentación está a tu nombre?</span>
            </div>
            <v-divider class="mb-4"></v-divider>
            <v-radio-group v-model="isOwnDocumentation" row class="mt-0">
              <v-radio label="Sí, está a mi nombre (tutor registrado)" :value="true"></v-radio>
              <v-radio label="No, a nombre de mi pareja o familiar" :value="false"></v-radio>
            </v-radio-group>
            <p v-if="formSubmitted && isOwnDocumentation === null" class="caption red--text mb-2">Obligatorio</p>
            <div v-if="isOwnDocumentation === false">
              <p class="subtitle-2 font-weight-medium mb-2">¿Quién figura en la documentación?</p>
              <div v-if="coTutors.length > 0">
                <v-radio-group v-model="selectedCoTutorId">
                  <v-radio
                    v-for="ct in coTutors"
                    :key="ct.id"
                    :label="`${ct.firstName} ${ct.lastName} — ${displayRelationshipType(ct.relationshipType)}`"
                    :value="ct.id"
                  ></v-radio>
                  <v-radio label="Agregar otra persona" :value="-1"></v-radio>
                </v-radio-group>
              </div>
              <div v-if="coTutors.length === 0 || selectedCoTutorId === -1" class="mt-2">
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="newCoTutor.firstName" label="Nombre" outlined dense :error="formSubmitted && !newCoTutor.firstName"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="newCoTutor.lastName" label="Apellido" outlined dense :error="formSubmitted && !newCoTutor.lastName"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select v-model="newCoTutor.relationshipType" :items="relationshipTypes" item-text="label" item-value="value" label="Tipo de relación" outlined dense :error="formSubmitted && !newCoTutor.relationshipType"></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="newCoTutor.rut" label="RUT (opcional)" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-file-input v-model="newCoTutor.file" label="Documento de vínculo (opcional)" outlined dense show-size></v-file-input>
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Envío -->
        <v-card v-if="selectedService" outlined class="mb-4">
          <v-card-text class="pa-5">
            <p v-if="!user.dataBank" class="body-2 error--text mb-3 d-flex align-center">
              <v-icon size="16" color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
              El usuario debe tener datos bancarios registrados antes de enviar el reembolso.
            </p>
            <v-btn color="primary" depressed large :disabled="!user.dataBank" @click="validateAndSubmit()">
              <v-icon left>mdi-upload</v-icon>
              Agregar reembolso
            </v-btn>
          </v-card-text>
        </v-card>

      </div>
    </div>
    <declaracion-jurada-modal
      ref="djModal"
      :iso-country="userIsoCountry"
      @completed="onDJCompleted"
    ></declaracion-jurada-modal>
    <add-bank-data-modal
      v-if="user"
      ref="bankModal"
      :user-id="user.id"
      @saved="onBankSaved"
      @error="showErrorSnackbar"
    ></add-bank-data-modal>
    <add-refund-documents-modal
      v-if="selectedRefundId && selectedRefundServiceId"
      ref="addDocsModal"
      :refund-id="selectedRefundId"
      :service-id="selectedRefundServiceId"
      @saved="onDocsSaved"
      @error="onDocsError"
    ></add-refund-documents-modal>

    <v-snackbar v-model="snackbar" multi-line :timeout="2000" :color="snackbarColor">
      {{ snackbarMessage }}

      <template v-slot:action>
        <v-btn text color="white" @click="snackbar = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import UsersService from '../services/users.service';
import MembershipsService from '../services/memberships.service';
import BanksService from '../services/banks.service';
import ServicesService from '../services/services.service';
import MembershipStatusChip from '../components/MembershipStatusChip';
import PlanStatusChip from '../components/PlanStatusChip';
import RefundsServices from '../services/refunds.service';
import FileUtils from '../utils/file.utils';
import { RELATIONSHIP_TYPE_OPTIONS, displayRelationshipType } from '../utils/relationship-type.utils';
import AddBankDataModal from '../components/AddBankDataModal';
import DeclaracionJuradaModal from '../components/DeclaracionJuradaModal';
import AddRefundDocumentsModal from '../components/AddRefundDocumentsModal';

export default {
  name: 'RegisterRefund',
  components: {
    MembershipStatusChip,
    PlanStatusChip,
    AddBankDataModal,
    DeclaracionJuradaModal,
    AddRefundDocumentsModal
  },
  data() {
    return {
      creatingRefund: false,
      search: null,
      loading: false,
      userRefunds: [],
      loadingRefunds: false,
      selectedRefundId: null,
      selectedRefundServiceId: null,
      selectedService: null,
      membershipSelected: { name: '' },
      user: null,
      userCandidates: [],
      requiredAttachments: [],
      attachmentFiles: {},
      optionalAttachments: [],
      optionalAttachmentFiles: {},
      jurAttachment: null,
      declaracionJurada: null,
      djExamenesFile: null,
      comments: '',
      jurOpened: false,
      userIsoCountry: 'CL',
      isOwnDocumentation: null,
      coTutors: [],
      selectedCoTutorId: null,
      newCoTutor: { firstName: '', lastName: '', relationshipType: null, rut: '', file: null },
      relationshipTypes: RELATIONSHIP_TYPE_OPTIONS,
      selectedDate: null,
      snackbarMessage: '',
      snackbar: false,
      snackbarColor: '',
      formSubmitted: false,
      eligibilityMap: {},
      checkingEligibility: false
    };
  },
  computed: {
    today() {
      return new Date().toISOString().split('T')[0];
    },
    accountNumberDisplay() {
      // Vista de administración: la cuenta se muestra completa porque se usa
      // para ejecutar la transferencia del reembolso.
      const num = this.user && this.user.dataBank && this.user.dataBank.accountNumber
        ? String(this.user.dataBank.accountNumber)
        : null;
      return num || '-';
    },
    jurIsIncomplete() {
      return this.jurOpened && !this.declaracionJurada;
    },
    jurCardColor() {
      if (this.declaracionJurada) return 'primary lighten-5';
      if (this.jurIsIncomplete) return 'error lighten-5';
      return 'white';
    },
    jurColor() {
      if (this.declaracionJurada) return 'primary';
      if (this.jurIsIncomplete) return 'error';
      return undefined;
    },
    jurTextClass() {
      if (this.declaracionJurada) return 'primary--text';
      if (this.jurIsIncomplete) return 'error--text';
      return 'grey--text text--darken-2';
    },
    jurSubtitle() {
      if (this.declaracionJurada) return 'Completado';
      if (this.jurIsIncomplete) return 'Incompleto';
      return null;
    }
  },
  watch: {
    async membershipSelected(membership) {
      this.selectedService = null;
      this.eligibilityMap = {};
      if (!membership || !membership.id || !membership.servicesToRefund) return;

      this.checkingEligibility = true;
      await Promise.all(
        membership.servicesToRefund.map(async (service) => {
          const res = await RefundsServices.checkEligibility(membership.petId, service.id);
          this.$set(
            this.eligibilityMap,
            service.id,
            res.status === 'ok' ? res.data : { allowed: true, reason: null }
          );
        })
      );
      this.checkingEligibility = false;
    },
    selectedService(service) {
      this.requiredAttachments = [];
      this.attachmentFiles = {};
      this.optionalAttachments = [];
      this.optionalAttachmentFiles = {};
      this.jurAttachment = null;
      this.declaracionJurada = null;
      this.djExamenesFile = null;
      this.comments = '';
      this.jurOpened = false;
      this.isOwnDocumentation = null;
      this.selectedCoTutorId = null;
      this.newCoTutor = { firstName: '', lastName: '', relationshipType: null, rut: '', file: null };
      if (this.$refs.djModal) this.$refs.djModal.resetForm();
      if (service) {
        this.loadRequiredAttachments(service.id).catch(() => {
          this.snackbar = true;
          this.snackbarColor = 'red';
          this.snackbarMessage = 'No se pudieron cargar los documentos requeridos';
        });
      }
    }
  },
  methods: {
    displayRelationshipType(type) {
      return displayRelationshipType(type);
    },
    openBankModal() {
      this.$refs.bankModal.open();
    },
    openJurModal() {
      this.jurOpened = true;
      this.$refs.djModal.open();
    },
    onBankSaved(bankData) {
      this.user.dataBank = bankData;
      this.snackbar = true;
      this.snackbarColor = 'green';
      this.snackbarMessage = 'Datos bancarios guardados con éxito';
    },
    showErrorSnackbar() {
      this.snackbar = true;
      this.snackbarColor = 'red';
      this.snackbarMessage = 'No se pudieron guardar los datos bancarios';
    },
    onDocsSaved() {
      this.snackbar = true;
      this.snackbarColor = 'green';
      this.snackbarMessage = 'Documentos agregados con éxito';
    },
    onDocsError() {
      this.snackbar = true;
      this.snackbarColor = 'red';
      this.snackbarMessage = 'Ocurrió un error con los documentos del reembolso';
    },
    async uploadRefund() {
      try {
        this.creatingRefund = true;

        const requiredFiles = await Promise.all(
          this.requiredAttachments.map(async (a) => {
            const file = this.attachmentFiles[a.id];
            return {
              id: a.id,
              base64Image: await FileUtils.getBase64(file),
              fileName: file.name
            };
          })
        );
        const optionalFiles = await Promise.all(
          this.optionalAttachments
            .flatMap(a => (this.optionalAttachmentFiles[a.id] || []).map(file => ({ id: a.id, file })))
            .map(async ({ id, file }) => ({
              id,
              base64Image: await FileUtils.getBase64(file),
              fileName: file.name
            }))
        );
        const examFile = this.djExamenesFile
          ? [{
              id: this.djExamenesFile.attachmentTypeId,
              base64Image: await FileUtils.getBase64(this.djExamenesFile.file),
              fileName: this.djExamenesFile.file.name
            }]
          : [];
        const files = [...requiredFiles, ...optionalFiles, ...examFile];

        const refund = {
          userId: this.user.id,
          membershipId: this.membershipSelected.id,
          petId: this.membershipSelected.petId,
          serviceId: this.selectedService.id,
          userBankDetailsId: this.user.dataBank ? this.user.dataBank.id : null,
          draft: false,
          dgStatus: 'tbd',
          attachments: files,
          createdAt: this.selectedDate,
          updatedAt: this.selectedDate,
          status: 0,
          ...(this.comments && { comments: this.comments }),
          ...(this.declaracionJurada && { declaracionJurada: this.declaracionJurada })
        };
        const res = await RefundsServices.createRefund(refund);
        if (res.status === 'ok') {
          this.snackbar = true;
          this.snackbarColor = 'green';
          this.snackbarMessage = 'El reembolso fue creado con éxito';
          return true;
        } else {
          this.snackbar = true;
          this.snackbarColor = 'red';
          this.snackbarMessage = 'El reembolso no pudo ser creado';
          return false;
        }
      } catch {
        this.snackbar = true;
        this.snackbarColor = 'red';
        this.snackbarMessage = 'El reembolso no pudo ser creado';
        return false;
      } finally {
        this.creatingRefund = false;
      }
    },
    async loadUserRefunds(userId) {
      this.loadingRefunds = true;
      const res = await RefundsServices.getRefunds(1, 50, [0, 1, 2], null, 'referenceIndex', 'desc', userId);
      this.userRefunds = res.status === 'ok' ? (res.data.result || []) : [];
      this.loadingRefunds = false;
    },
    openAddDocsModal(refund) {
      if (!refund.service || !refund.service.id) {
        this.snackbar = true;
        this.snackbarColor = 'red';
        this.snackbarMessage = 'Este reembolso no tiene un servicio asociado';
        return;
      }
      this.selectedRefundId = refund.id;
      this.selectedRefundServiceId = refund.service.id;
      this.$nextTick(() => this.$refs.addDocsModal.open());
    },
    resetUserState() {
      this.user = null;
      this.userCandidates = [];
      this.userRefunds = [];
      this.selectedRefundId = null;
      this.selectedRefundServiceId = null;
      this.membershipSelected = { name: '' };
      this.selectedService = null;
      this.coTutors = [];
      this.isOwnDocumentation = null;
      this.selectedCoTutorId = null;
      this.newCoTutor = { firstName: '', lastName: '', relationshipType: null, rut: '', file: null };
    },
    async searchUser() {
      this.resetUserState();

      if (!this.search) return;

      this.loading = true;
      try {
        const userRes = await UsersService.getUsersBySearchToRefund(this.search);
        const users = (userRes.status === 'ok' && userRes.data.users) || [];
        if (users.length === 0) {
          this.snackbar = true;
          this.snackbarColor = 'red';
          this.snackbarMessage = 'Usuario no encontrado';
          return;
        }
        if (users.length === 1) {
          await this.selectUser(users[0]);
        } else {
          // Email is not a uniqueness key — more than one user can share it,
          // so the admin picks the correct one before continuing.
          this.userCandidates = users;
        }
      } catch {
        this.snackbar = true;
        this.snackbarColor = 'red';
        this.snackbarMessage = 'Algo salió mal, inténtalo más tarde';
      } finally {
        this.loading = false;
      }
    },
    async selectCandidate(candidate) {
      this.loading = true;
      try {
        await this.selectUser(candidate);
      } catch {
        this.snackbar = true;
        this.snackbarColor = 'red';
        this.snackbarMessage = 'Algo salió mal, inténtalo más tarde';
      } finally {
        this.loading = false;
      }
    },
    async selectUser(candidate) {
      const user = { ...candidate };

      const bankDataRes = await BanksService.getUserBankData(user.id);
      const bankData = bankDataRes.status === 'ok' ? bankDataRes.data : null;
      user.dataBank = (bankData && bankData.bankId) ? bankData : null;
      this.userIsoCountry = (bankData && bankData.isoCountry) || 'CL';

      const membershipsRes = await MembershipsService.getActiveServicesByUser(user.id);
      if (membershipsRes.status === 'ok') {
        user.memberships = membershipsRes.data.map(m => ({
          id: m.id,
          petId: m.petId,
          name: m.pet.name,
          species: m.pet.species,
          status: m.status,
          planName: m.plan.name,
          servicesToRefund: m.plan.servicePlanDetails.map(spd => ({
            id: spd.service.id,
            servicesName: spd.service.title
          }))
        }));
      } else {
        user.memberships = [];
      }

      const coTutorsRes = await UsersService.getCoTutorsByUser(user.id);
      this.coTutors = coTutorsRes.status === 'ok' ? coTutorsRes.data : [];

      this.user = user;
      this.userCandidates = [];
      // Scoped by user id, not email — email can belong to more than one user.
      this.loadUserRefunds(user.id);
    },

    async loadRequiredAttachments(serviceId) {
      const res = await ServicesService.getServiceRequiredInputs(serviceId);
      if (res.status === 'ok' && res.data && res.data.serviceType) {
        const all = res.data.serviceType.requiredAttachments;
        const jur = all.find(a => a.name.toLowerCase().includes('jur')) || null;
        const required = all.filter(a => a.name !== 'Documento adicional' && !a.name.toLowerCase().includes('jur'));
        const optional = all.filter(a => a.name === 'Documento adicional');

        const requiredFiles = {};
        required.forEach(a => { requiredFiles[a.id] = null; });
        const optionalFiles = {};
        optional.forEach(a => { optionalFiles[a.id] = []; });

        this.jurAttachment = jur;
        this.declaracionJurada = null;
        this.attachmentFiles = requiredFiles;
        this.requiredAttachments = required;
        this.optionalAttachmentFiles = optionalFiles;
        this.optionalAttachments = optional;
      }
    },

    async onDJCompleted({ formData, examenesFile }) {
      this.declaracionJurada = formData;
      this.djExamenesFile = null;
      if (!examenesFile) return;
      try {
        const typesRes = await RefundsServices.getAttachmentTypes();
        if (typesRes.status !== 'ok') {
          this.snackbar = true;
          this.snackbarColor = 'red';
          this.snackbarMessage = 'No se pudo obtener los tipos de adjunto. El resultado de exámenes no se adjuntará.';
          return;
        }
        const examType = typesRes.data.find(t => t.name === 'Resultado de exámenes');
        if (!examType) {
          this.snackbar = true;
          this.snackbarColor = 'red';
          this.snackbarMessage = 'No se encontró el tipo "Resultado de exámenes". El archivo no se adjuntará.';
          return;
        }
        this.djExamenesFile = { file: examenesFile, attachmentTypeId: examType.id };
      } catch {
        this.snackbar = true;
        this.snackbarColor = 'red';
        this.snackbarMessage = 'Error inesperado al procesar el resultado de exámenes.';
      }
    },

    validateForm() {
      if (!this.selectedDate) return 'Selecciona una fecha de solicitud';
      if (this.jurAttachment && !this.declaracionJurada) return 'Completa la declaración jurada';
      if (this.isOwnDocumentation === null) return 'Indica si la documentación está a tu nombre';
      if (!this.isOwnDocumentation) {
        if (this.coTutors.length > 0 && this.selectedCoTutorId === null) return 'Selecciona quién figura en la documentación';
        if (this.coTutors.length === 0 || this.selectedCoTutorId === -1) {
          if (!this.newCoTutor.firstName || !this.newCoTutor.lastName || !this.newCoTutor.relationshipType) {
            return 'Completa los datos del co-tutor';
          }
        }
      }
      const missingAttachment = this.requiredAttachments.find(a => !this.attachmentFiles[a.id]);
      if (missingAttachment) return `Adjunta el documento: ${missingAttachment.name}`;
      return null;
    },

    async validateAndSubmit() {
      this.formSubmitted = true;
      const validationError = this.validateForm();
      if (validationError) {
        this.snackbar = true;
        this.snackbarColor = 'red';
        this.snackbarMessage = validationError;
        return;
      }

      if (!this.isOwnDocumentation && (this.selectedCoTutorId === -1 || this.coTutors.length === 0)) {
        try {
          const payload = {
            firstName: this.newCoTutor.firstName,
            lastName: this.newCoTutor.lastName,
            relationshipType: this.newCoTutor.relationshipType,
            ...(this.newCoTutor.rut && { rut: this.newCoTutor.rut })
          };
          if (this.newCoTutor.file) {
            payload.file = {
              base64: await FileUtils.getBase64(this.newCoTutor.file),
              contentType: this.newCoTutor.file.type,
              extension: this.newCoTutor.file.name.split('.').pop()
            };
          }
          const coTutorRes = await UsersService.addCoTutor(this.user.id, payload);
          if (coTutorRes.status !== 'ok') {
            this.snackbar = true;
            this.snackbarColor = 'red';
            this.snackbarMessage = 'No se pudo registrar el co-tutor';
            return;
          }
        } catch {
          this.snackbar = true;
          this.snackbarColor = 'red';
          this.snackbarMessage = 'No se pudo registrar el co-tutor';
          return;
        }
      }

      const success = await this.uploadRefund();
      if (success) {
        this.resetForm();
        this.formSubmitted = false;
      }
    },

    resetForm() {
      this.selectedService = null;
      this.selectedDate = null;
      this.requiredAttachments = [];
      this.attachmentFiles = {};
      this.optionalAttachments = [];
      this.optionalAttachmentFiles = {};
      this.jurAttachment = null;
      this.declaracionJurada = null;
      this.djExamenesFile = null;
      this.comments = '';
      this.jurOpened = false;
      this.isOwnDocumentation = null;
      this.coTutors = [];
      this.selectedCoTutorId = null;
      this.newCoTutor = { firstName: '', lastName: '', relationshipType: null, rut: '', file: null };
      this.eligibilityMap = {};
      this.checkingEligibility = false;
    }
  }
};
</script>
